import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Check, X, Loader2, ShoppingCart, Tag } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { tierFor, countSessions } from '../../utils/campPricing';

// Native select styling shared by learner + SD 308 pickers.
// Radix Select portals its dropdown outside the Dialog DOM boundary,
// which triggers the Dialog's onInteractOutside handler and closes the
// modal before the selection can register. Native <select> has no portaling.
const nativeSelectCls = 'mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-rsc-navy focus:outline-none focus:ring-2 focus:ring-rsc-navy/30';

const DEFAULT_TIERS = [
  { min_sessions: 1,  max_sessions: 1,    price: 69 },
  { min_sessions: 2,  max_sessions: 4,    price: 59 },
  { min_sessions: 5,  max_sessions: 9,    price: 49 },
  { min_sessions: 10, max_sessions: null, price: 45 },
];

const DAY_NAMES = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
const sessionLabel = s => s === 'full_day' ? 'Full Day' : s === 'am' ? 'AM' : 'PM';

const SD308_SCHOOLS = [
  'Bednarcik Junior High School', 'Plank Junior High School',
  'Thompson Junior High School', 'Traughber Junior High School',
  'Murphy Junior High School', 'Boulder Hill Elementary School',
  'Churchill Elementary School', 'East View Elementary School',
  'Fox Chase Elementary School', 'Grande Park Elementary School',
  'Homestead Elementary School', 'Hunt Club Elementary School',
  'The Lakewood Creek Elementary School', 'Long Beach Elementary School',
  'Old Post Elementary School', 'Prairie Point Elementary School',
  'Southbury Elementary School', 'Wheatlands Elementary School',
  "Wolf's Crossing Elementary School", '__NOT_LISTED__',
];

export default function MultiWeekCartModal({
  open,
  onClose,
  campSessions,
  camps,
  campDetails,
  getSelectedSectionForCamp,
  onAdded,
}) {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { refreshCart } = useCart();

  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [extendedCare, setExtendedCare] = useState('none');
  const [sd308Schools, setSd308Schools] = useState({});
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !user || !api) return;
    let cancelled = false;
    setLearnersLoading(true);
    api.get('/learners/')
      .then(res => {
        if (cancelled) return;
        const list = res.data?.results || res.data || [];
        setLearners(list);
        if (list.length === 1) setSelectedLearner(String(list[0].id));
      })
      .catch(() => {})
      .finally(() => !cancelled && setLearnersLoading(false));
    return () => { cancelled = true; };
  }, [open, user, api]);

  const activeCamps = useMemo(() => {
    return Object.entries(campSessions)
      .filter(([, sessions]) => Object.keys(sessions).length > 0)
      .map(([campId, sessions]) => {
        const camp = camps.find(c => String(c.id) === String(campId));
        const section = camp ? getSelectedSectionForCamp(camp) : null;
        if (!camp || !section) return null;
        const sessionList = Object.entries(sessions).map(([day, type]) => ({ session: type, day: parseInt(day) }));
        const sessionCount = countSessions(sessions);
        const isSd308 = section.location_name?.includes('Bednarcik') || section.location_name?.includes('Plank');
        return { campId, camp, section, sessions, sessionList, sessionCount, isSd308 };
      })
      .filter(Boolean);
  }, [campSessions, camps, getSelectedSectionForCamp]);

  const totalSessions = useMemo(() =>
    activeCamps.reduce((sum, e) => sum + e.sessionCount, 0), [activeCamps]);

  const tier = useMemo(() => tierFor(totalSessions, DEFAULT_TIERS), [totalSessions]);

  const hasFullDay = useMemo(() =>
    activeCamps.some(e => Object.values(e.sessions).some(s => s === 'full_day')), [activeCamps]);

  const extendedCareCost = useMemo(() => {
    if (extendedCare === 'none' || !hasFullDay) return 0;
    return activeCamps.reduce((sum, e) => {
      const isK2 = (e.camp?.grade_bands || []).some(g => ['K', 'K-2'].includes(g));
      const weeklyRate = Number(e.section.extended_care_weekly ?? (isK2 ? 50 : 100));
      const is4Day = e.section.location_name?.includes('Bednarcik') || e.section.location_name?.includes('Plank');
      const fullWeekSessions = is4Day ? 8 : 10;
      const raw = (weeklyRate / fullWeekSessions) * e.sessionCount;
      return sum + (extendedCare === 'both' ? raw : raw / 2);
    }, 0);
  }, [activeCamps, extendedCare, hasFullDay]);

  const basePrice = activeCamps.reduce((sum, e) => sum + e.sessionCount * tier.price, 0);
  const topRateTotal = activeCamps.reduce((sum, e) => sum + e.sessionCount * DEFAULT_TIERS[0].price, 0);
  const tierSavings = topRateTotal - basePrice;
  const subtotal = basePrice + extendedCareCost;
  const couponDiscount = useMemo(() => {
    if (!couponApplied) return 0;
    if (couponApplied.discount_type === 'percent')
      return Math.round(subtotal * (couponApplied.discount_value / 100) * 100) / 100;
    if (couponApplied.discount_type === 'flat')
      return Math.min(couponApplied.discount_value, subtotal);
    return 0;
  }, [couponApplied, subtotal]);
  const totalPrice = subtotal - couponDiscount;

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) { toast.error('Enter a coupon code'); return; }
    try {
      const res = await api.post('/coupons/validate/', { code, subtotal: String(totalPrice.toFixed(2)) });
      const data = res.data || {};
      setCouponApplied({
        code: data.code || code,
        label: data.label || `${data.discount_value}${data.discount_type === 'percent' ? '%' : '$'} off`,
        discount_type: data.discount_type,
        discount_value: Number(data.discount_value || 0),
      });
      toast.success(`Coupon applied: ${data.label || code}`);
    } catch (err) {
      const payload = err.response?.data || {};
      toast.error(payload.error_message || payload.detail || 'Invalid coupon code');
    }
  };
  const clearCoupon = () => { setCouponApplied(null); setCouponCode(''); };

  const needsSd308 = activeCamps.some(e => e.isSd308 && !sd308Schools[e.campId]);

  const handleAdd = async () => {
    if (activeCamps.length === 0) return;
    if (user && !selectedLearner) { toast.error('Please select a learner'); return; }
    if (needsSd308) { toast.error('Please select a school for SD 308 locations'); return; }

    setSubmitting(true);
    let companionSessions = 0;
    try {
      for (const entry of activeCamps) {
        const payload = {
          camp_section_id: entry.section.id,
          learner_id: user ? selectedLearner : null,
          selected_days: Object.entries(entry.sessions).map(([day, session]) => ({
            day: parseInt(day), session,
          })),
          extended_care: hasFullDay ? extendedCare : 'none',
          coupon_code: couponApplied?.code || undefined,
          companion_sessions: companionSessions,
          ...(entry.isSd308 && sd308Schools[entry.campId] ? { sd308_school: sd308Schools[entry.campId] } : {}),
        };
        await api.post(`/camps/${entry.camp.id}/add-to-cart/`, payload);
        companionSessions += entry.sessionCount;
      }
      await refreshCart();
      toast.success(`${totalSessions} session${totalSessions !== 1 ? 's' : ''} added to cart!`, {
        duration: 2500,
        action: { label: 'View cart', onClick: () => navigate('/cart') },
      });
      onAdded?.();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Failed to add to cart';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg w-full bg-[#0a1f3a] text-white border-rsc-yellow/20 p-0 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 bg-rsc-yellow/10 border-b border-rsc-yellow/20 flex-shrink-0">
          <div>
            <div className="text-[10px] font-bold text-rsc-yellow/80 uppercase tracking-wider">Add to Cart</div>
            <div className="text-base font-bold text-white flex items-center gap-2">
              {totalSessions} session{totalSessions !== 1 ? 's' : ''} · ${tier.price}/session
              {totalSessions >= 10 && (
                <span className="text-[10px] font-extrabold bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">BEST RATE</span>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Sessions by camp */}
          <div className="space-y-2">
            {activeCamps.map(({ campId, camp, section, sessions, sessionCount }) => (
              <div key={campId} className="rounded-lg bg-white/[0.04] border border-white/10 p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-white truncate">{camp.name}</div>
                    <div className="text-[10px] text-white/50">{section.location_name}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs font-bold text-rsc-yellow">${(sessionCount * tier.price).toFixed(2)}</div>
                    <div className="text-[10px] text-white/40">{sessionCount} session{sessionCount !== 1 ? 's' : ''}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(sessions).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([day, sess]) => (
                    <span key={day} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-rsc-navy/60 border border-white/10 px-1.5 py-0.5 rounded">
                      {DAY_NAMES[parseInt(day)] || `Day ${day}`}
                      <span className="text-rsc-yellow">·</span>
                      {sessionLabel(sess)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Learner */}
          {user && (
            <div className="bg-white rounded-lg p-4">
              <Label className="text-rsc-navy">Select Learner *</Label>
              {learnersLoading ? (
                <div className="text-xs text-gray-500 mt-2 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
              ) : learners.length === 0 ? (
                <div className="text-xs text-gray-600 mt-2">
                  No learners found.{' '}
                  <Button variant="link" className="px-1 py-0 h-auto text-xs" onClick={() => navigate('/parent/learners')}>Add a learner</Button>
                </div>
              ) : (
                <select
                  value={selectedLearner}
                  onChange={e => setSelectedLearner(e.target.value)}
                  className={nativeSelectCls}
                >
                  <option value="">Choose a learner</option>
                  {learners.map(l => (
                    <option key={l.id} value={String(l.id)}>
                      {l.first_name} {l.last_name} ({l.grade || 'N/A'})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* SD-308 school picker for restricted locations */}
          {activeCamps.filter(e => e.isSd308).map(entry => (
            <div key={entry.campId} className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
              <Label className="text-amber-200 text-xs">School for {entry.section.location_name} (SD 308 verification) *</Label>
              <select
                value={sd308Schools[entry.campId] || ''}
                onChange={e => setSd308Schools(prev => ({ ...prev, [entry.campId]: e.target.value }))}
                className={nativeSelectCls}
              >
                <option value="">Select your student's school</option>
                {SD308_SCHOOLS.map(s => (
                  <option key={s} value={s}>{s === '__NOT_LISTED__' ? 'Not listed (out-of-district)' : s}</option>
                ))}
              </select>
            </div>
          ))}

          {/* Extended care */}
          {hasFullDay && (
            <div className="bg-white rounded-lg p-4">
              <Label className="text-rsc-navy font-semibold">Extended Care</Label>
              <p className="text-xs text-gray-500 mb-2">For full-day sessions. Prorated to your session count.</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'none',  label: 'None' },
                  { value: 'early', label: 'Early Drop-off 8–9am' },
                  { value: 'late',  label: 'Late Pick-up 4–5pm' },
                  { value: 'both',  label: 'Both' },
                ].map(opt => (
                  <button key={opt.value} type="button" onClick={() => setExtendedCare(opt.value)}
                    className={`text-left text-xs px-3 py-2 rounded-lg border transition-all ${extendedCare === opt.value ? 'border-rsc-yellow bg-rsc-yellow/10 text-rsc-navy font-bold' : 'border-gray-200 text-gray-600 hover:border-rsc-yellow/40'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Coupon */}
          <div className="bg-white rounded-lg p-4">
            <Label className="flex items-center gap-1.5 text-rsc-navy"><Tag className="w-3.5 h-3.5" /> Coupon Code</Label>
            <div className="flex gap-2 mt-1">
              <Input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter code" disabled={!!couponApplied}
                className="text-gray-900"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }} />
              {!couponApplied
                ? <Button onClick={applyCoupon} variant="outline" size="sm" className="text-gray-900 border-gray-300">Apply</Button>
                : <Button onClick={clearCoupon} variant="outline" size="sm" className="text-gray-900 border-gray-300"><X className="h-4 w-4" /></Button>}
            </div>
            {couponApplied && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <Check className="h-3 w-3" /> {couponApplied.label}
              </p>
            )}
          </div>

          {/* Price summary */}
          <div className="rounded-lg bg-white/[0.04] border border-white/10 p-4 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-white/60">{totalSessions} session{totalSessions !== 1 ? 's' : ''} × ${tier.price}</span>
              <span className="font-bold text-white">${basePrice.toFixed(2)}</span>
            </div>
            {extendedCareCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Extended care</span>
                <span className="text-white">+${extendedCareCost.toFixed(2)}</span>
              </div>
            )}
            {tierSavings > 0.01 && (
              <div className="flex justify-between text-xs text-emerald-400">
                <span>Tier discount vs. $69/session</span>
                <span>−${tierSavings.toFixed(2)}</span>
              </div>
            )}
            {couponDiscount > 0 && (
              <div className="flex justify-between text-xs text-emerald-400">
                <span>Coupon ({couponApplied.code})</span>
                <span>−${couponDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-extrabold pt-1.5 border-t border-white/10">
              <span className="text-white">Total</span>
              <span className="text-rsc-yellow">${totalPrice.toFixed(2)}</span>
            </div>
          </div>

          <Button size="lg"
            className="w-full bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow/90 font-extrabold h-12 text-base shadow-lg"
            disabled={submitting || totalSessions === 0 || (user && !selectedLearner) || needsSd308}
            onClick={handleAdd}>
            {submitting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding…</>
              : <><ShoppingCart className="w-4 h-4 mr-2" /> Add {totalSessions} session{totalSessions !== 1 ? 's' : ''} to Cart</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
